import {Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output} from '@angular/core';
import {select, Store} from '@ngrx/store';

import {first, takeUntil} from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import {Subject, forkJoin} from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ProductInfo, ProductInfoForKey, ProductOptions, ProductKey, PROFESSIONAL_PRODUCT_KEYS, ToothArea, MEMOTAIN_PRODUCT_KEYS } from 'libs/product/src/lib/models';
import { DataTransferMethod, TreatmentModel } from 'libs/treatments/src/lib/models/treatment.model';
import { ACTION } from 'libs/tm-common/src/lib/constants';
import { AppState } from 'libs/tm-common/src/lib/store/reducers';
import { TreatmentsService } from 'libs/treatments/src/lib/services/treatments.service';
import { NotificationService } from 'libs/tm-common/src/lib/services/notification.service';
import { PatientModel } from 'libs/tm-common/src/lib/models/patient.model';
import { AddTreatment, TreatmentSelected } from 'libs/treatments/src/lib/store/treatment.actions';
import { selectEstimatedCosts } from 'libs/order/src/lib/store/order.selectors';
import { ClearEstimatedCosts, EstimatedCostsRequested, EstimatedCostsUpdated } from 'libs/order/src/lib/store/order.actions';
import { Anamnese } from 'libs/anamnese/src/lib/models/anamnese.model';
import { selectAnamnesePatient, selectAnamneseProductKey, selectAnamneseProductOptions, selectAnamneseQuestionairePreparedAnswers, selectAnamneseQuestionnaireOrcadentAnswers } from 'libs/anamnese/src/lib/store/anamnese.selectors';
import { TreatmentIdUpdatedAction } from 'libs/anamnese/src/lib/store/anamnese.actions';
import { NewPatientTreatment } from 'libs/patients/src/lib/store/patient.actions';
import { QuestionairePrintDialogComponent, QuestionairePrintDialogData } from 'libs/questionaire/src/lib/questionaire-print/questionaire-print-dialog.component';
import { EUCountryCode, SapAddress, SapCostEstimate } from 'libs/sap/src/lib/models/sap.model';
import { QuestionnaireOrcadentDialogComponent } from 'libs/questionaire/src/lib/orcadent/component/questionnaire-orcadent-dialog.component';


const OrcadentCostEstimate: SapCostEstimate = {
	ItemPriceSum: 299,
	DiscountedItemPriceSum: 0,
	DiscountFactor: 1,
	ShippingCost: 0,
	Vat: 0,
	Total: 299
}

enum OrderProcessStatus{
	NOT_ORDERED = 0,
	PROCESSING = 1,
	PROCESSED = 2,
	ERROR = 3
}

@Component({
	selector: 'tm2-anamnese-order',
	templateUrl: './anamnese-order.component.html',
	styleUrls: ['./_anamnese-order.component.scss']
})
export class AnamneseOrderComponent implements OnInit, OnChanges, OnDestroy {

	@Input() anamnese: Anamnese;
	@Input() isSetupProduct: boolean;
	@Output() next = new EventEmitter<any>();

	OrderProcessStatus = OrderProcessStatus;
	ProductKey = ProductKey;
	DataTransferMethod = DataTransferMethod;
	MEMOTAIN_PRODUCT_KEYS = MEMOTAIN_PRODUCT_KEYS;

	productKey: ProductKey;
	estimatedCosts: SapCostEstimate;
	hasValidCostEstimate = false;
	options: ProductOptions;
	disabledBack = false;
	processed;
	orderProcessStatus = OrderProcessStatus.NOT_ORDERED;
	buttonText;
	ACTION = ACTION;
	billingAddr: SapAddress;
	shippingAddr: SapAddress;
	destroy$ = new Subject<void>();

	constructor(
		private store: Store<AppState>,
		private treatmentsService: TreatmentsService,
		private notificationService: NotificationService,
		private translate: TranslateService,
		@Inject('CONFIG') public config,
		public dialog: MatDialog
	) { }

	ngOnInit() {
		this.processed = false;
		this.store.pipe(select(selectAnamneseProductOptions), takeUntil(this.destroy$))
			.subscribe((options: ProductOptions) => {
				this.options = options;
			});

		// final prices
		this.store.pipe(select(selectEstimatedCosts), takeUntil(this.destroy$))
		.subscribe(estimatedCosts => {
			this.estimatedCosts = estimatedCosts;
			this.hasValidCostEstimate = this.estimatedCosts?.Total >= 0;
		});
	}

	ngOnChanges() {
		this.productKey = this.anamnese.product.key as ProductKey;

		// initialize as long as anamnese is not been processed
		if (!this.processed) {
			this.billingAddr = this.anamnese.product.handling.billing;
			this.shippingAddr = this.anamnese.product.handling.shipping;
			this.buttonText = this.isSetupProduct ? 'ORDER.setup' : 'ORDER.order';
			if(this.anamnese.product.key === ProductKey.ORCADENT_SCHIENE_ASSESSMENT)
			{
				const costEstimate: SapCostEstimate = OrcadentCostEstimate;
				this.store.dispatch(new EstimatedCostsUpdated({ estimatedCosts: costEstimate }));
			}else{
				this.store.dispatch(
					new EstimatedCostsRequested({
						productKey: this.anamnese.product.key as ProductKey,
						shippingMethod: this.anamnese.product.handling.shipment.Method,
						country: this.shippingAddr.Country,
						transferMethod: this.anamnese.product.options.dataTransfer,
						uj: this.anamnese.product.options.uj ?? ToothArea.NONE,
						lj: this.anamnese.product.options.lj ?? ToothArea.NONE,
					})
				);
			}
		}else{
			this.buttonText = this.anamnese.product.options.dataTransfer === DataTransferMethod.UPLOAD ? 'gotoTreatment' : 'Next';
		}
	}

	ngOnDestroy() {
		this.destroy$.next();
	}

	onAction(action) {
		switch (action.type) {
			case ACTION.BACK:
				this.store.dispatch(new ClearEstimatedCosts());
				this.next.emit({type: ACTION.BACK});
				break;
			case ACTION.NEXT:
				if (!this.processed) {
					this.orderProcessStatus = OrderProcessStatus.PROCESSING;
					this.processAnamnese();
					// notify anamnese.component about ordered treatment
					this.next.emit({type: ACTION.SUBMIT, payload: undefined});
				} else {
					this.next.emit({type: ACTION.NEXT, payload: this.anamnese});
				}
				break;
		}
	}

	processAnamnese() {
		this.next.emit({type: ACTION.DISABLE_PREVIOUS_STEPS});
		this.disabledBack = true;
		this.treatmentsService.createTreatment()
			.subscribe({
				next: (resp: TreatmentModel) => this.onAnamneseSuccess(resp),
				error: error => this.onAnamneseError(error)
			});
	}

	onAnamneseSuccess(treatment) {
		this.store.pipe(select(selectAnamnesePatient), first()).subscribe((anamnesePatient) => {
			const patient: PatientModel = anamnesePatient;

			// Handle UI and Reduxstore
			this.processed = true;
			this.orderProcessStatus = OrderProcessStatus.PROCESSED;
			// ...and enrich treatment with patient object
			treatment['patient'] = patient;

			// store treatment
			this.store.dispatch(new AddTreatment({treatment: treatment as TreatmentModel}));
			this.store.dispatch(new TreatmentIdUpdatedAction({treatmentId: treatment._id}));
			this.store.dispatch(new TreatmentSelected(treatment._id));
			this.store.dispatch(new NewPatientTreatment({patientId: patient._id || '', treatmentId: treatment._id}));
		});
	}

	onAnamneseError(error) {
		this.orderProcessStatus = OrderProcessStatus.ERROR;
		this.notificationService.showErrorMessage(
			"ErrorSubmitOrder"
		);
		console.error(error);
	}


	openProfessionalQuestionnaireDialog() {
		forkJoin({
			questionaireAnswers: this.store.pipe(select(selectAnamneseQuestionairePreparedAnswers), first()),
			productKey: this.store.pipe(select(selectAnamneseProductKey), first()),
			patient: this.store.pipe(select(selectAnamnesePatient), first())
		}).subscribe((value) => {
			const dialogData: QuestionairePrintDialogData = {
				answers: value.questionaireAnswers,
				productKey: value.productKey,
				patient: value.patient
			};

			this.dialog.open(QuestionairePrintDialogComponent,
				{data: dialogData, maxWidth: '100vw', width: '100%', height: '100%'});
		});
	}

	openOrcadentQuestionnaireDialog(){
		this.store.pipe(select(selectAnamneseQuestionnaireOrcadentAnswers), first()).subscribe((questionnaireData) => {
			const initialWidth = '95%';
			const initialHeight = '95%';
			this.dialog.open(QuestionnaireOrcadentDialogComponent,
				{data: {
					questionnaireData: questionnaireData,
					width: initialWidth,
					height: initialHeight
				},
				width: initialWidth, height: initialHeight});
		});
	}

	// Currently, this function is only relevant for aligner products, since there is no
	// e-mail notification for other products after ordering.
	getProductName( treatment: TreatmentModel ) : string
	{
		const productInfo: ProductInfo = ProductInfoForKey[treatment.product.key as ProductKey];
		let nameHtml = productInfo?.name;
		if(productInfo?.variantName){
			nameHtml += " ";
			nameHtml += this.translate.instant(productInfo.variantName);
		}
		return nameHtml;
	}

	protected getTextForCountryCode(Country: string) {
		return Country === EUCountryCode.Greece_EL ? EUCountryCode.Greece : Country;
	}

	protected calculateDiscountInPercent(){
		return ((1.0 - this.estimatedCosts.DiscountFactor) * 100).toFixed(2);
	}

	protected isProfessionalProduct(): boolean{
		return PROFESSIONAL_PRODUCT_KEYS.includes( this.productKey );
	}

	protected isOrcadentProduct(): boolean{
		return this.productKey === ProductKey.ORCADENT_SCHIENE_ASSESSMENT;
	}
}
